import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { BaseStoreFacade } from '@city/data-access';
import { MenuItemNested, MenuSlug } from '@core/const';
import { MenuNestedService } from '@core/data-access';
import { BonusWidgetComponent } from '@sdvor/shared/ui/v2';
import { BonusSystemFeatureAvailableService, CurrentProfileService } from '@user/feature';
import { combineLatest, OperatorFunction, pipe } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

export interface MyAccountMenuItem extends MenuItemNested {
  isBonusItem?: boolean;
  bonusBalance?: number;
}

@Component({
  selector: 'sd-my-account-navigation',
  templateUrl: './my-account-navigation.component.html',
  styleUrls: ['./my-account-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, RouterLink, RouterLinkActive, BonusWidgetComponent],
})
export class MyAccountNavigationComponent implements OnInit {
  menuItems$ = this.menuNestedService
    .getMenuBySlugValue(MenuSlug.MyAccountNavigation)
    .pipe(filter(Boolean), this.handleBonusItem(), this.handlePromocodesItem());

  constructor(
    private bonusSystemFeatureAvailableService: BonusSystemFeatureAvailableService,
    private currentProfileService: CurrentProfileService,
    private menuNestedService: MenuNestedService,
    private baseStoreFacade: BaseStoreFacade,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.baseStoreFacade
      .getActiveCode()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((baseStoreCode) => {
        this.menuNestedService.loadMenuBySlug(MenuSlug.MyAccountNavigation, baseStoreCode);
      });
  }

  /**
   * Данный метод находит ссылку на страницу с бонусами
   * - добавляет к ссылке дополнительную информацию с кол-вом бонусов и сегментом пользователя;
   * - удаляет ссылку из массива, если программа лояльности выключена;
   */
  handleBonusItem(): OperatorFunction<MenuItemNested[], MyAccountMenuItem[]> {
    return pipe(
      switchMap((items: MenuItemNested[]) =>
        combineLatest({
          isFeatureAvailable: this.bonusSystemFeatureAvailableService.isFeatureAvailableForUser(),
          bonusBalanceInfo: this.currentProfileService.get().pipe(map((user) => user?.bonusBalance)),
          isForeman: this.currentProfileService
            .get()
            .pipe(map((user) => user?.user?.additionalOptions?.isForeman ?? false)),
        }).pipe(
          map(({ isFeatureAvailable, bonusBalanceInfo, isForeman }) => {
            return items.reduce((acc, item) => {
              // todo: лучше завязываться на url, поэтому когда задача SPA-2308 доедет до прода,
              // необходимо будет в страпи поменять тайтл с "BONUS_LINK" на "программа лояльности"
              // и тут использовать тайтл из страпи
              if (item.url === '/my-account/points') {
                if (!isForeman) {
                  if (isFeatureAvailable && bonusBalanceInfo && typeof bonusBalanceInfo.value === 'number') {
                    acc.push({
                      ...item,
                      title: 'Программа лояльности',
                    });
                  }
                }
              } else {
                acc.push(item);
              }

              return acc;
            }, [] as MyAccountMenuItem[]);
          }),
        ),
      ),
    );
  }

  /**
   * Данный метод находит ссылку на страницу с промокодами удаляет ее из массива,
   * если у текущего пользователя нет сегмента бригадира
   */
  handlePromocodesItem(): OperatorFunction<MenuItemNested[], MyAccountMenuItem[]> {
    return pipe(
      switchMap((items: MenuItemNested[]) =>
        this.currentProfileService
          .get()
          .pipe(map((profile) => profile.user?.additionalOptions?.isForeman ?? false))
          .pipe(
            map((isForeman) => {
              return items.reduce((acc, item) => {
                if (item.url !== '/my-account/promocodes' || isForeman) {
                  acc.push(item);
                }

                return acc;
              }, [] as MyAccountMenuItem[]);
            }),
          ),
      ),
    );
  }
}
